import {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";

import LocalStorageService from "@services/localStorage";
import ChatService from "@services/api/chat";
import { UserContext } from "@contexts/User";
import { ChatContext } from "@contexts/Messages";
import { generateUUID } from "@utils/helpers";
import ResourceService from "@services/api/resource";
import {
  CHAT_MESSAGE_TYPES,
  SUPER_ADMIN_SENDER_NAME,
  USER_ROLES,
} from "@utils/consts";
import chatGraphqlInstance from "@services/api/chat.graphql";
import { useLocation } from "react-router-dom";
import profileGraphqlInstance from "@services/api/profile.graphql";
import { GlobalChatContext } from "@contexts/GlobalMessages";

// Chat attachments are immutable once a message is sent, so resolved
// results are safe to reuse for the life of the tab across remounts,
// chat-room revisits, and chatMessagesRefresh triggers instead of
// re-issuing a resources?entityId=... call per message every time.
const chatAttachmentsCache = new Map();

const getChatAttachments = (accountId, entityId, entityType, isGlobal) => {
  const cacheKey = `${accountId}|${entityId}|${entityType}|${isGlobal}`;
  if (!chatAttachmentsCache.has(cacheKey)) {
    chatAttachmentsCache.set(
      cacheKey,
      ResourceService.getResourcesByEntity(
        accountId,
        entityId,
        entityType,
        isGlobal,
      ).catch((err) => {
        chatAttachmentsCache.delete(cacheKey);
        throw err;
      }),
    );
  }
  return chatAttachmentsCache.get(cacheKey);
};

const getMaxUnreadParticipant = async (participants, chatRoomMessageCount) => {
  if (!participants || participants.length === 0) return null;

  return participants.reduce((maxP, current) => {
    const messageCount = Number(chatRoomMessageCount);
    const readCount = Number(current.readMessageCount);
    const unreadCount = messageCount - readCount;

    // attach unreadCount for convenience
    current.unreadCount = unreadCount;

    if (!maxP || unreadCount > maxP.unreadCount) {
      return current;
    }
    return maxP;
  }, null);
};

const prepareChatRoom = async (chatRoomParam, user) => {
  const { messageCount, ...chatRoom } = { ...chatRoomParam };
  let unreadMessageCount = messageCount ? Number(messageCount) : 0;

  if (chatRoom.participants) {
    const currentUserParticipant = await getMaxUnreadParticipant(
      chatRoom.participants.filter((participant) =>
        user?.userIds?.includes(participant.userId),
      ),
      messageCount,
    );
    if (currentUserParticipant) {
      const sendMessageCount = currentUserParticipant.sendMessageCount
        ? Number(currentUserParticipant.sendMessageCount)
        : 0;
      const readMessageCount = currentUserParticipant.readMessageCount
        ? Number(currentUserParticipant.readMessageCount)
        : 0;
      unreadMessageCount =
        unreadMessageCount - sendMessageCount - readMessageCount;

      chatRoom.participants = chatRoom.participants.filter(
        (participant) =>
          participant.userId !== chatRoom?.currentUserProfile?.userId,
      );
    }
  }

  if (chatRoom.lastMessage) {
    chatRoom.lastMessage.sentAt = Number(chatRoom.lastMessage.sentAt);
  } else {
    chatRoom.lastMessage = {
      sentAt: Number(chatRoom.createdAt),
      messageText: "",
      type: CHAT_MESSAGE_TYPES.START_CHAT,
    };
  }

  return {
    ...chatRoom,
    unreadMessageCount,
  };
};

const prepareChatMessage = async (chatMessage) => {
  if (
    (chatMessage.type === CHAT_MESSAGE_TYPES.TEXT_ATTACHMENT ||
      chatMessage.type === CHAT_MESSAGE_TYPES.ATTACHMENT) &&
    !chatMessage.attachments?.length
  ) {
    const attachments = (
      await getChatAttachments(
        chatMessage.globalChatRoomId
          ? chatMessage.globalChatRoomId
          : chatMessage.senderName === SUPER_ADMIN_SENDER_NAME
            ? chatMessage.chatRoomId
            : chatMessage.accountId,
        chatMessage.id,
        "chatAttachment",
        chatMessage.hasOwnProperty("globalChatRoomId"),
      )
    ).map((v) => ({ ...v, size: Number(v.size) }));

    return {
      ...chatMessage,
      attachments,
      sentAt: Number(chatMessage.sentAt),
    };
  }

  return {
    ...chatMessage,
    sentAt: Number(chatMessage.sentAt),
  };
};

export const useChatList = (
  {
    isPopulateChatParticipants = false,
    isPopulateChatParticipantProfiles = false,
    isPopulateLastMessage = false,
    allowReadMessageEventEmitting = false,
    isCacheEnabled = false,
    chatListRefresh = false,
  } = {},
  filterFn,
) => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const chatId = query.get("chatId");

  const [chatList, setChatList] = useState(undefined);
  // Load cached chat list from async storage on mount or when enabled
  useEffect(() => {
    let isMounted = true;
    if (isCacheEnabled) {
      (async () => {
        const cache = await LocalStorageService.getChatRooms();
        if (isMounted && cache?.length) {
          setChatList(cache);
        }
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [isCacheEnabled]);

  const [isLoading, setIsLoading] = useState(true);
  const pendingNewChats = useRef([]);
  const { profile, user } = useContext(UserContext);
  const { socket, openedChatRoomId } = useContext(ChatContext);
  const openedChatRoomIdRef = useRef(openedChatRoomId);
  const isUnmounted = useRef();
  const isCacheMounted = useRef();

  // Selection updates read state locally; it must not trigger a list fetch.
  // Pending fetches still need the latest selection when they complete.
  useEffect(() => {
    openedChatRoomIdRef.current = openedChatRoomId;
    setChatList((prev) => {
      const openedChat = prev?.find((room) => room.id === openedChatRoomId);
      if (!openedChat?.unreadMessageCount) return prev;
      return prev.map((room) =>
        room.id === openedChatRoomId
          ? { ...room, unreadMessageCount: 0 }
          : room,
      );
    });
  }, [openedChatRoomId]);

  useEffect(() => {
    if (isCacheEnabled && chatList && isCacheMounted.current) {
      LocalStorageService.setChatRooms(chatList);
      return;
    }

    isCacheMounted.current = true;
  }, [chatList]);

  const fetchChatList = useCallback(async () => {
    if (!profile) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const chatRoomsResponse = await ChatService.getChatRoomsByUserId(
      profile.accountId,
      profile.userId,
      isPopulateLastMessage,
      isPopulateChatParticipants,
    );

    const chatRoomsResponseFiltered = chatRoomsResponse.filter((chatRoom) => {
      if (chatRoom?.organizerName === SUPER_ADMIN_SENDER_NAME) {
        return true;
      }
      return chatRoom?.participants?.length > 1;
    });

    let preparedChatRooms = chatRoomsResponseFiltered.map((chatRoom) =>
      prepareChatRoom(chatRoom, user),
    );

    preparedChatRooms = (await Promise.all(preparedChatRooms))
      // eslint-disable-next-line no-nested-ternary
      .sort((a, b) =>
        a.lastMessage.sentAt < b.lastMessage.sentAt
          ? 1
          : b.lastMessage.sentAt < a.lastMessage.sentAt
            ? -1
            : 0,
      );

    if (!isUnmounted.current) {
      // The backend doesn't persist "read" state for regular (non-Global)
      // chat rooms, so a fresh server fetch always reports the room's real,
      // never-decremented unread count — including for the chat the user
      // currently has open. `chatRoomUpdated`/`ParticipantChatListUpdate`
      // (below) trigger this refetch on every message in any room, which
      // would otherwise silently resurrect the open chat's badge moments
      // after `newMessageHandler` just cleared it. Force it back to 0 here
      // too, since from the client's perspective an open chat is read
      // regardless of what the server's stale count says.
      setChatList(
        preparedChatRooms.map((room) =>
          room.id === openedChatRoomIdRef.current
            ? { ...room, unreadMessageCount: 0 }
            : room,
        ),
      );
      setIsLoading(false);
    }
  }, [
    profile,
    isPopulateLastMessage,
    isPopulateChatParticipants,
    user,
  ]);

  useEffect(() => {
    fetchChatList();
  }, [fetchChatList, chatListRefresh]);

  useEffect(() => {
    if (
      !socket?.current ||
      socket.current.disconnected ||
      !profile ||
      !openedChatRoomId ||
      !allowReadMessageEventEmitting
    ) {
      return;
    }

    if (!chatId) {
      return;
    }
    socket.current.emit("readMessages", openedChatRoomId);
  }, [allowReadMessageEventEmitting, chatId, openedChatRoomId, profile, socket]);

  useEffect(() => {
    if (!socket?.current || socket.current.disconnected || !profile) {
      return;
    }

    const newChatRoomHandler = async (chatRoom) => {
      if (!Array.isArray(chatList)) return;
      if (chatList.find((chat) => chat.id === chatRoom.id)) {
        return;
      }

      if (
        !Array.isArray(chatRoom.participants) ||
        chatRoom.participants.length < 2
      ) {
        pendingNewChats.current = [...pendingNewChats.current, chatRoom];
        return;
      }

      const preparedChatRoom = await prepareChatRoom(chatRoom, user);

      if (isUnmounted.current) {
        return;
      }

      setChatList((prev) =>
        prev ? [preparedChatRoom, ...prev] : [preparedChatRoom],
      );
    };

    const newChatParticipantHandler = async (chatParticipant) => {
      // this is used so we don't create new item in the list with one participant while adding participant
      const pendingChat = pendingNewChats.current.find(
        (v) => v.id === chatParticipant.chatRoomId,
      );
      if (pendingChat) {
        if (
          Array.isArray(chatList) &&
          chatList.find((chat) => chat.id === pendingChat.id)
        ) {
          pendingNewChats.current = pendingNewChats.current.filter(
            (v) => v.id !== pendingChat.id,
          );
          return;
        }

        const newChat = {
          ...pendingChat,
          participants: pendingChat.participants
            ? [...pendingChat.participants, chatParticipant]
            : [chatParticipant],
        };

        const chatRoomsResponse = await ChatService.getChatRoomsByUserId(
          profile.accountId,
          profile.userId,
          isPopulateLastMessage,
          isPopulateChatParticipants,
        );

        const chatRoomsResponseFiltered = chatRoomsResponse.filter(
          (chatRoom) => chatRoom.participants.length > 1,
        );
        let preparedChatRooms = await chatRoomsResponseFiltered.map(
          (chatRoom) => prepareChatRoom(chatRoom, user),
        );
        preparedChatRooms = (await Promise.all(preparedChatRooms))
          // eslint-disable-next-line no-nested-ternary
          .sort((a, b) =>
            a.lastMessage.sentAt < b.lastMessage.sentAt
              ? 1
              : b.lastMessage.sentAt < a.lastMessage.sentAt
                ? -1
                : 0,
          );

        if (filterFn && typeof filterFn === "function") {
          preparedChatRooms = filterFn(preparedChatRooms);
        }

        if (!isUnmounted.current) {
          setChatList(preparedChatRooms);
          setIsLoading(false);
        }
        pendingNewChats.current = pendingNewChats.current.filter(
          (v) => v.id !== newChat.id,
        );
      }
    };

    const newMessageHandler = (chatMessage) => {
      const newMessageChat = Array.isArray(chatList)
        ? chatList.find((chat) => chat.id === chatMessage.chatRoomId)
        : undefined;
      if (!newMessageChat) {
        return;
      }

      if (!user?.userIds?.includes(chatMessage.senderId)) {
        const params = new URLSearchParams(window.location.search);
        const chatIdParams = params.get("chatId");
        if (chatMessage.chatRoomId === openedChatRoomId && chatIdParams) {
          newMessageChat.unreadMessageCount = 0;
          if (allowReadMessageEventEmitting) {
            socket.current.emit("readMessages", chatMessage.chatRoomId);
          }
        } else {
          newMessageChat.unreadMessageCount += 1;
        }
      }

      newMessageChat.lastMessage = {
        ...chatMessage,
        sentAt: Number(chatMessage.sentAt),
      };

      if (!isUnmounted.current) {
        setChatList((prev) => [
          newMessageChat,
          ...prev.filter((chat) => chat.id !== chatMessage.chatRoomId),
        ]);
      }
    };

    const messagesReadHandler = (readEvent) => {
      if (!chatList) {
        return;
      }

      const chatListCopy = [...chatList];

      const readMessagesChat = chatListCopy.find(
        (chat) => chat.id === readEvent.chatRoomId,
      );
      if (!readMessagesChat || readMessagesChat.unreadMessageCount === 0) {
        return;
      }

      readMessagesChat.unreadMessageCount = 0;

      if (!isUnmounted.current) {
        setChatList(chatListCopy);
      }
    };

    const chatRoomDeletedHandler = async (chatRoomId) => {
      if (
        !Array.isArray(chatList) ||
        !chatList.some((chat) => chat.id === chatRoomId)
      ) {
        return;
      }

      if (!isUnmounted.current) {
        setChatList((prev) => prev.filter((chat) => chat.id !== chatRoomId));
      }
    };

    socket.current.on("newChatRoom", newChatRoomHandler);
    socket.current.on("newChatParticipant", newChatParticipantHandler);
    socket.current.on("newMessage", newMessageHandler);
    socket.current.on("messagesRead", messagesReadHandler);
    socket.current.on("chatRoomDeleted", chatRoomDeletedHandler);
    socket.current.on("ParticipantChatListUpdate", fetchChatList);
    socket.current.on("chatRoomUpdated", fetchChatList);

    // eslint-disable-next-line consistent-return
    return () => {
      socket.current.off("newChatRoom", newChatRoomHandler);
      socket.current.off("newChatParticipant", newChatParticipantHandler);
      socket.current.off("newMessage", newMessageHandler);
      socket.current.off("messagesRead", messagesReadHandler);
      socket.current.off("chatRoomDeleted", chatRoomDeletedHandler);
      socket.current.off("ParticipantChatListUpdate", fetchChatList);
      socket.current.off("chatRoomUpdated", fetchChatList);
    };
  }, [
    chatList,
    socket,
    profile,
    openedChatRoomId,
    isPopulateChatParticipantProfiles,
    allowReadMessageEventEmitting,
    fetchChatList,
    filterFn,
    isPopulateChatParticipants,
    isPopulateLastMessage,
    user,
  ]);

  const updateChatRoom = useCallback(
    async (accountId, chatRoom, cb) => {
      const chatListCopy = [...chatList];
      const updatingChatIndex = chatListCopy.findIndex(
        (chat) => chat.id === chatRoom.id,
      );
      if (updatingChatIndex === -1) {
        return;
      }

      const updateResponse = await ChatService.updateChatRoom(
        accountId,
        chatRoom,
      );
      chatListCopy[updatingChatIndex] = {
        ...chatListCopy[updatingChatIndex],
        isArchived: updateResponse.isArchived,
        organizerName: updateResponse.organizerName,
      };

      setChatList(chatListCopy);
      if (cb && typeof cb === "function") {
        cb();
      }
    },
    [chatList],
  );

  return useMemo(() => {
    // TODO: investigate why sometimes there are chat duplicates
    const chats = chatList
      ? chatList.filter(
          (chat, index) =>
            chatList.findIndex((c) => c.id === chat.id) === index,
        )
      : [];
    return [chats, { isLoading, updateChatRoom }];
  }, [chatList, isLoading]);
};

export const useChatMessages = ({
  chatRoomId,
  filterFn,
  chatMessagesRefresh = false,
  isGlobalChat = false,
  isDataFetchingEnabled = true,
}) => {
  if (!isDataFetchingEnabled) {
    return [undefined, { isLoading: false }];
  }
  const [chatMessages, setChatMessages] = useState(undefined);
  // Load cached messages from async storage on mount or when chatRoomId changes
  useEffect(() => {
    let isMounted = true;
    (async () => {
      let cache = undefined;
      if (isGlobalChat) {
        cache = await LocalStorageService.getGlobalChatRoomMessages(chatRoomId);
      } else {
        cache = await LocalStorageService.getChatRoomMessages(chatRoomId);
      }
      if (isMounted && cache) {
        setChatMessages(cache);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [chatRoomId, isGlobalChat]);

  const [isLoading, setIsLoading] = useState(true);
  const [updatedReadBadgeId, setUpdatedReadBadgeId] = useState();
  const { profile, user } = useContext(UserContext);
  const { socket } = useContext(ChatContext);
  const { socketForGlobalMessage } = useContext(GlobalChatContext);
  const [initial, setInitial] = useState(true);
  const [isDataOver, setIsDataOver] = useState(false);
  const [date, setDate] = useState(null);
  const isUnmounted = useRef();
  const isCacheMounted = useRef();

  useEffect(() => {
    if (chatMessages && isCacheMounted.current) {
      if (isGlobalChat) {
        LocalStorageService.setGlobalChatRoomMessages(chatRoomId, chatMessages);
      } else {
        LocalStorageService.setChatRoomMessages(chatRoomId, chatMessages);
      }
      return;
    }

    isCacheMounted.current = true;
  }, [chatMessages]);

  const handleChatMessages = async (date = null) => {
    if (!profile || !chatRoomId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let chatRoomMessagesResponse = [];
    if (isGlobalChat) {
      chatRoomMessagesResponse = await chatGraphqlInstance.getChatRoomMessages(
        chatRoomId,
        date,
      );
    } else {
      chatRoomMessagesResponse = await ChatService.getChatRoomMessages(
        profile.accountId,
        chatRoomId,
        date,
      );
    }
    if (chatRoomMessagesResponse.length < 20) {
      setIsDataOver(true);
    }
    const preparedChatMessagesPromises = chatRoomMessagesResponse.map(
      (chatMessage) => prepareChatMessage(chatMessage),
    );
    let preparedChatMessages = (await Promise.all(preparedChatMessagesPromises))
      // eslint-disable-next-line no-nested-ternary
      .sort((a, b) => (a.sentAt < b.sentAt ? 1 : b.sentAt < a.sentAt ? -1 : 0));

    if (filterFn && typeof filterFn === "function") {
      preparedChatMessages = filterFn(preparedChatMessages);
    }

    if (!isUnmounted.current) {
      setChatMessages((prev) => {
        if (initial) {
          return preparedChatMessages;
        }
        return [...prev, ...preparedChatMessages];
      });
      setIsLoading(false);
    }
  };

  const handleLoadMoreMessages = async (newDate) => {
    setDate(newDate);
    setInitial(false);
  };

  useEffect(() => {
    if (date !== null && !initial) {
      handleChatMessages(date);
    }
  }, [initial, date]);

  useEffect(() => {
    (async () => {
      handleChatMessages();
      return () => {
        isUnmounted.current = true;
      };
    })();
  }, [profile, chatRoomId, chatMessagesRefresh]);

  useEffect(() => {
    if (isGlobalChat) {
      if (
        !socketForGlobalMessage?.current ||
        socketForGlobalMessage.current.disconnected
      ) {
        return;
      }
    } else {
      if (!socket?.current || socket.current.disconnected) {
        return;
      }
    }

    const newMessageHandler = async (chatMessage) => {
      if (isGlobalChat) {
        if (chatMessage.globalChatRoomId !== chatRoomId) {
          return;
        }
      } else {
        if (chatMessage.chatRoomId !== chatRoomId) {
          return;
        }
      }

      const existingMessage = chatMessages?.find(
        (v) => v.id === chatMessage.id,
      );

      if (existingMessage) {
        if (!existingMessage.isPending) {
          return;
        }

        const preparedMessage = await prepareChatMessage({
          ...chatMessage,
          attachments: existingMessage.attachments,
          isPending: false,
        });
        if (!isUnmounted.current) {
          setChatMessages((prev) => [
            preparedMessage,
            ...prev.filter((v) => v.id !== preparedMessage.id),
          ]);
        }

        return;
      }

      const preparedNewMessage = await prepareChatMessage(chatMessage);

      if (!isUnmounted.current) {
        setChatMessages((prev) =>
          prev ? [preparedNewMessage, ...prev] : [preparedNewMessage],
        );
      }
    };

    const participantDeletedHandler = () => {
      handleChatMessages();
    };

    socket.current.on("newMessage", newMessageHandler);
    socketForGlobalMessage.current.on(
      "newGlobalChatMessage",
      newMessageHandler,
    );
    socket.current.on(
      "participantMessagesRead",
      participantMessagesReadHandler,
    );
    socket.current.on("participantDeleteMessage", participantDeletedHandler);
    socketForGlobalMessage.current.on(
      "globalParticipantDeleteMessage",
      participantDeletedHandler,
    );

    // eslint-disable-next-line consistent-return
    return () => {
      socket.current.off("newMessage", newMessageHandler);
      socketForGlobalMessage.current.off(
        "newGlobalChatMessage",
        newMessageHandler,
      );
      socket.current.off(
        "participantMessagesRead",
        participantMessagesReadHandler,
      );
      socket.current.off("participantDeleteMessage", participantDeletedHandler);
      socketForGlobalMessage.current.off(
        "globalParticipantDeleteMessage",
        participantDeletedHandler,
      );
    };
  }, [chatMessages, socket, profile, chatRoomId, socketForGlobalMessage]);

  const createChatMessage = useCallback(
    async (newMessage, cb) => {
      const messageId = generateUUID();
      const messageAttachments = newMessage.attachments?.length
        ? newMessage.attachments.map((attachment) => ({
            id: attachment.fileName.split(".")[0],
            accountId: newMessage.accountId,
            entityId: messageId,
            entityType: "chatAttachment",
            resourceName: attachment.originalFileName.split(".")[0],
            resourceType: attachment.fileName.split(".").pop(),
            contentType: attachment.mimetype,
            size: attachment.size,
          }))
        : [];

      const preparedNewMessage = await prepareChatMessage({
        id: messageId,
        ...newMessage,
        attachments: messageAttachments,
      });

      setChatMessages((prev) =>
        prev
          ? [{ ...preparedNewMessage, isPending: true }, ...prev]
          : [{ ...preparedNewMessage, isPending: true }],
      );

      const attachmentsPromises = messageAttachments.map((attachment) =>
        ResourceService.createResource(
          preparedNewMessage.accountId,
          attachment,
        ),
      );

      await Promise.all(attachmentsPromises);
      await ChatService.createChatRoomMessage(preparedNewMessage.accountId, {
        id: preparedNewMessage.id,
        accountId: preparedNewMessage.accountId,
        senderName: preparedNewMessage.senderName,
        senderId: preparedNewMessage.senderId,
        chatRoomId: preparedNewMessage.chatRoomId,
        messageText: preparedNewMessage.messageText,
        type: preparedNewMessage.type,
        sentAt: preparedNewMessage.sentAt,
      });

      if (cb && typeof cb === "function") {
        cb();
      }
    },
    [chatMessages],
  );

  const createGlobalChatMessage = useCallback(
    async (newMessage, cb) => {
      const messageId = generateUUID();
      const messageAttachments = newMessage.attachments?.length
        ? newMessage.attachments.map((attachment) => ({
            id: attachment.fileName.split(".")[0],
            accountId: newMessage.globalChatRoomId,
            entityId: messageId,
            entityType: "chatAttachment",
            resourceName: attachment.originalFileName.split(".")[0],
            resourceType: attachment.fileName.split(".").pop(),
            contentType: attachment.mimetype,
            size: attachment.size,
            isGlobalChat: true,
          }))
        : [];

      const preparedNewMessage = await prepareChatMessage({
        id: messageId,
        ...newMessage,
        attachments: messageAttachments,
      });

      setChatMessages((prev) =>
        prev
          ? [{ ...preparedNewMessage, isPending: true }, ...prev]
          : [{ ...preparedNewMessage, isPending: true }],
      );

      const attachmentsPromises = messageAttachments.map((attachment) =>
        ResourceService.createResource(
          preparedNewMessage.globalChatRoomId,
          attachment,
        ),
      );

      await Promise.all(attachmentsPromises);
      await chatGraphqlInstance.createChatChatMessageGlobal({
        id: preparedNewMessage.id,
        senderName: preparedNewMessage.senderName,
        senderId: preparedNewMessage.senderId,
        globalChatRoomId: preparedNewMessage.globalChatRoomId,
        messageText: preparedNewMessage.messageText,
        type: preparedNewMessage.type,
        sentAt: preparedNewMessage.sentAt,
      });

      if (cb && typeof cb === "function") {
        cb();
      }
    },
    [chatMessages],
  );

  const participantMessagesReadHandler = async (readEvent) => {
    if (chatMessages && readEvent.chatRoomId === chatRoomId) {
      await chatGraphqlInstance
        .getUnreadMessageCount(
          readEvent.chatRoomId,
          readEvent.accountId,
          readEvent.userId,
        )
        .then((res) => {
          const messageData = chatMessages.filter((x) =>
            user?.userIds?.includes(x.senderId),
          );
          if (messageData) {
            setUpdatedReadBadgeId(messageData?.[res]?.id);
          }
        });
    }
  };

  return useMemo(() => {
    // TODO: investigate why sometimes there are message duplicates
    const messages = chatMessages
      ? chatMessages.filter(
          (message, index) =>
            chatMessages.findIndex((m) => m.id === message.id) === index,
        )
      : [];
    return [
      messages,
      {
        createChatMessage,
        isLoading,
        updatedReadBadgeId,
        createGlobalChatMessage,
        handleLoadMoreMessages,
        isDataOver,
      },
    ];
  }, [chatMessages, isLoading, updatedReadBadgeId, handleLoadMoreMessages]);
};

export const useChatRoomCreate = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const createChatRoom = useCallback(async (newChat, chatParticipant, cb) => {
    setIsLoading(true);

    const chatRoomResponse = await ChatService.createChatRoom(
      newChat.accountId,
      {
        isArchived: newChat.isArchived,
        organizerName: newChat.organizerName,
        organizerId: newChat.organizerId,
        accountId: newChat.accountId,
      },
    );

    const chatRoomParticipant = await ChatService.createChatRoomParticipant(
      chatRoomResponse.accountId,
      {
        chatRoomId: chatRoomResponse.id,
        accountId: chatRoomResponse.accountId,
        name: chatParticipant.name,
        userId: chatParticipant.userId,
      },
    );

    if (!isUnmounted.current) {
      setIsLoading(false);
    }

    if (cb && typeof cb === "function") {
      cb({ ...chatRoomResponse, id: chatRoomParticipant.chatRoomId });
    }
  }, []);

  return [createChatRoom, { isLoading }];
};

export const useGroupChatRoomCreate = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const createGroupChatRoom = useCallback(
    async (newChat, chatParticipant, cb) => {
      setIsLoading(true);
      const chatRoomResponse = await ChatService.createChatRoom(
        newChat.accountId,
        {
          isArchived: newChat.isArchived,
          organizerName: newChat.organizerName,
          organizerId: newChat.organizerId,
          accountId: newChat.accountId,
          name: newChat.groupName ? newChat.groupName : undefined,
        },
      );

      for (const participant of chatParticipant) {
        await ChatService.createChatRoomParticipant(
          chatRoomResponse.accountId,
          {
            chatRoomId: chatRoomResponse.id,
            accountId: chatRoomResponse.accountId,
            name: participant.name,
            userId: participant.userId,
          },
        );
      }

      if (!isUnmounted.current) {
        setIsLoading(false);
      }

      if (cb && typeof cb === "function") {
        cb(chatRoomResponse);
      }
    },
    [],
  );

  return [createGroupChatRoom, { isLoading }];
};

export const useChatRoomDelete = () => {
  const [isLoading, setIsLoading] = useState(false);

  const deleteChatRoom = useCallback(async (accountId, chatRoomId, cb) => {
    setIsLoading(true);

    await ChatService.deleteChatRoom(accountId, chatRoomId);

    setIsLoading(false);

    if (cb && typeof cb === "function") {
      cb();
    }
  }, []);

  return [deleteChatRoom, { isLoading }];
};

export const useChatAttachments = (
  isGlobalChatRoom = false,
  chatRoomId,
  filterFn,
) => {
  const [attachments, setAttachments] = useState();
  const { profile } = useContext(UserContext);
  const { socket } = useContext(ChatContext);
  const { socketForGlobalMessage } = useContext(GlobalChatContext);
  const [isLoading, setIsLoading] = useState(true);

  const isUnmounted = useRef();

  const fetchChatAttachments = useCallback(
    async (isGlobal = false) => {
      if (!profile) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      let chatAttachmentsResponse = [];

      if (isGlobal) {
        chatAttachmentsResponse =
          await chatGraphqlInstance.getAttachmentsByGlobalChatRoomId(
            chatRoomId,
          );
      } else {
        chatAttachmentsResponse = await ChatService.getChatRoomAttachments(
          profile.accountId,
          chatRoomId,
          isGlobal,
        );
      }

      if (isUnmounted.current) {
        return;
      }

      chatAttachmentsResponse = chatAttachmentsResponse
        .map((attachment) => ({
          ...attachment,
          sentAt: Number(attachment.sentAt),
          attachmentSize: Number(attachment.attachmentSize),
        }))
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) =>
          a.sentAt < b.sentAt ? 1 : b.sentAt < a.sentAt ? -1 : 0,
        );

      if (filterFn && typeof filterFn === "function") {
        chatAttachmentsResponse = filterFn(chatAttachmentsResponse);
      }

      setAttachments(chatAttachmentsResponse);
      setIsLoading(false);
    },
    [profile, chatRoomId],
  );

  useEffect(() => {
    fetchChatAttachments(isGlobalChatRoom);
  }, [profile, chatRoomId]);

  useEffect(() => {
    if (isGlobalChatRoom) {
      if (
        !socketForGlobalMessage?.current ||
        socketForGlobalMessage.current.disconnected
      ) {
        return;
      }
    } else {
      if (!socket?.current || socket.current.disconnected) {
        return;
      }
    }

    const newChatAttachmentHandler = async (chatAttachment) => {
      if (
        (chatAttachment?.hasOwnProperty("globalChatRoomId")
          ? chatAttachment?.globalChatRoomId
          : chatAttachment.chatRoomId) !== chatRoomId
      ) {
        return;
      }

      const preparedChatAttachment = {
        ...chatAttachment,
        sentAt: Number(chatAttachment.sentAt),
      };

      if (!isUnmounted.current) {
        setAttachments((prev) =>
          prev ? [preparedChatAttachment, ...prev] : [preparedChatAttachment],
        );
      }
    };

    const attachmentDeleteHandler = async (data) => {
      fetchChatAttachments(data);
    };

    socket.current.on("newChatAttachment", newChatAttachmentHandler);
    socketForGlobalMessage.current.on(
      "newGlobalChatAttachment",
      newChatAttachmentHandler,
    );
    socketForGlobalMessage.current.on(
      "globalParticipantDeleteAttachment",
      attachmentDeleteHandler,
    );
    socket.current.on("participantDeleteAttachment", attachmentDeleteHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      socket.current.off("newChatAttachment", newChatAttachmentHandler);
      socketForGlobalMessage.current.off(
        "newGlobalChatAttachment",
        newChatAttachmentHandler,
      );
      socketForGlobalMessage.current.off(
        "globalParticipantDeleteAttachment",
        attachmentDeleteHandler,
      );
      socket.current.off(
        "participantDeleteAttachment",
        attachmentDeleteHandler,
      );
    };
  }, [attachments, socket, profile, chatRoomId, socketForGlobalMessage]);

  // TODO: investigate why sometimes there are attachments duplicates
  return useMemo(() => {
    const result = attachments
      ? attachments.filter(
          (attachment, index) =>
            attachments.findIndex(
              (a) =>
                (a.hasOwnProperty("globalChatRoomId")
                  ? a.globalChatRoomId
                  : a.chatRoomId) ===
                  (attachment.hasOwnProperty("globalChatRoomId")
                    ? attachment?.globalChatRoomId
                    : attachment.chatRoomId) &&
                a.attachmentId === attachment.attachmentId,
            ) === index,
        )
      : [];

    return [result, { isLoading }];
  }, [attachments, isLoading]);
};

export const useGlobalChatRoomCreate = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const createGlobalChatRoom = useCallback(
    async (newChat, chatParticipant, cb) => {
      setIsLoading(true);
      const globalChatRoomResponse =
        await chatGraphqlInstance.createChatChatRoomGlobal({
          isArchived: newChat.isArchived,
          organizerName: newChat.organizerName,
          organizerId: newChat.organizerId,
        });

      await chatParticipant.forEach(
        async (participant) =>
          await chatGraphqlInstance.createChatChatParticipantGlobal({
            name: participant.name,
            userId: participant.userId,
            globalChatRoomId: globalChatRoomResponse.id,
          }),
      );

      if (!isUnmounted.current) {
        setIsLoading(false);
      }

      if (cb && typeof cb === "function") {
        cb(globalChatRoomResponse);
      }
    },
    [],
  );

  return [createGlobalChatRoom, { isLoading }];
};

export const useGroupChatRoomCreateForGlobal = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const createGroupChatRoomForGlobal = useCallback(
    async (newChat, chatParticipant, cb) => {
      setIsLoading(true);
      const globalChatRoomResponse =
        await chatGraphqlInstance.createChatChatRoomGlobal({
          isArchived: newChat.isArchived,
          organizerName: newChat.organizerName,
          organizerId: newChat.organizerId,
          name: newChat.groupName ? newChat.groupName : undefined,
        });

      await chatParticipant.forEach(
        async (participant) =>
          await chatGraphqlInstance.createChatChatParticipantGlobal({
            name: participant.name,
            userId: participant.userId,
            globalChatRoomId: globalChatRoomResponse.id,
          }),
      );

      if (!isUnmounted.current) {
        setIsLoading(false);
      }

      if (cb && typeof cb === "function") {
        cb(globalChatRoomResponse);
      }
    },
    [],
  );

  return [createGroupChatRoomForGlobal, { isLoading }];
};

const prepareGlobalChatRoom = async (
  chatRoomParam,
  userProfile,
  populateChatParticipantProfiles,
  isAllAccountsLogin = false,
) => {
  const { messageCount, ...chatRoom } = { ...chatRoomParam };
  let unreadMessageCount = messageCount ? Number(messageCount) : 0;

  if (chatRoom.participants) {
    const currentUserParticipant = chatRoom.participants.find(
      (participant) => participant.userId === userProfile.userId,
    );
    if (currentUserParticipant) {
      const sendMessageCount = currentUserParticipant.sendMessageCount
        ? Number(currentUserParticipant.sendMessageCount)
        : 0;
      const readMessageCount = currentUserParticipant.readMessageCount
        ? Number(currentUserParticipant.readMessageCount)
        : 0;
      unreadMessageCount =
        unreadMessageCount - sendMessageCount - readMessageCount;

      chatRoom.participants = chatRoom.participants.filter(
        (participant) => participant.userId !== userProfile.userId,
      );
    }

    if (populateChatParticipantProfiles && !isAllAccountsLogin) {
      const participantProfiles = chatRoom.participants.map((participant) =>
        profileGraphqlInstance.getAllAccountPerson({
          userId: participant.userId,
        }),
      );
      chatRoom.participantProfiles = await Promise.all(participantProfiles);
    }
  }

  if (chatRoom.lastMessage) {
    chatRoom.lastMessage = {
      ...chatRoom.lastMessage,
      sentAt: Number(chatRoom.lastMessage.sentAt),
    };
  } else {
    chatRoom.lastMessage = {
      sentAt: Number(chatRoom.createdAt),
      messageText: "",
      type: CHAT_MESSAGE_TYPES.START_CHAT,
    };
  }

  return {
    ...chatRoom,
    unreadMessageCount,
    profile:
      (chatRoom.participantProfiles &&
        chatRoom.participantProfiles?.flatMap((v) => v))?.[0] || [],
  };
};

export const useGlobalChatList = (
  {
    isPopulateChatParticipants = false,
    isPopulateChatParticipantProfiles = false,
    isPopulateLastMessage = false,
    allowReadMessageEventEmitting = false,
    isCacheEnabled = false,
    chatListRefresh = false,
  } = {},
  filterFn,
) => {
  const { profile, isAllAccountsLogin } = useContext(UserContext);
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const chatId = query.get("chatId");

  const { socketForGlobalMessage, openedChatRoomId } =
    useContext(GlobalChatContext);
  // Read via a ref inside `fetchGlobalChatList` instead of listing
  // `openedChatRoomId` as a dependency: the `useEffect(() => {
  // fetchGlobalChatList() }, [fetchGlobalChatList])` below re-invokes the
  // fetch whenever the callback's identity changes, so a reactive dependency
  // here would force a full refetch (and its `isLoading` flash) on every
  // single chat selection.
  const openedChatRoomIdRef = useRef(openedChatRoomId);
  useEffect(() => {
    openedChatRoomIdRef.current = openedChatRoomId;
    setGlobalChatList((prev) => {
      const openedChat = prev?.find((room) => room.id === openedChatRoomId);
      if (!openedChat?.unreadMessageCount) return prev;
      return prev.map((room) =>
        room.id === openedChatRoomId
          ? { ...room, unreadMessageCount: 0 }
          : room,
      );
    });
  }, [openedChatRoomId]);

  // stable flags
  const isCounsellor = profile?.type === USER_ROLES.COUNSELLOR_ROLE;
  const enabled = Boolean(isCounsellor && profile);

  // state/refs are ALWAYS created (no conditional returns)
  const [globalChatList, setGlobalChatList] = useState(undefined);
  // Load cached global chat list from async storage on mount or when enabled
  useEffect(() => {
    let isMounted = true;
    if (isCacheEnabled) {
      (async () => {
        const cache = await LocalStorageService.getChatRooms();
        if (isMounted && cache?.length) {
          setGlobalChatList(cache);
        }
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [isCacheEnabled]);
  const [isLoading, setIsLoading] = useState(true);

  const pendingNewChats = useRef([]);
  const isUnmounted = useRef(false);
  const isCacheMounted = useRef(false);

  // mark unmounted on cleanup
  useEffect(() => {
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  // persist cache
  useEffect(() => {
    if (!isCacheEnabled) return;
    if (!globalChatList) return;
    if (!isCacheMounted.current) {
      isCacheMounted.current = true;
      return;
    }
    LocalStorageService.setChatRooms(globalChatList);
  }, [isCacheEnabled, globalChatList]);

  const fetchGlobalChatList = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const chatRoomsResponse = await chatGraphqlInstance.getChatRoomsByUserId({
      userId: profile.userId,
      populateLastMessage: isPopulateLastMessage,
      populateChatParticipants: isPopulateChatParticipants,
    });

    const chatRoomsResponseFiltered = chatRoomsResponse.filter(
      (chatRoom) => chatRoom?.participants?.length > 1,
    );

    let preparedChatRooms = chatRoomsResponseFiltered.map((chatRoom) =>
      prepareGlobalChatRoom(
        chatRoom,
        profile,
        isPopulateChatParticipantProfiles,
        isAllAccountsLogin,
      ),
    );
    preparedChatRooms = (await Promise.all(preparedChatRooms)).sort((a, b) =>
      a.lastMessage.sentAt < b.lastMessage.sentAt
        ? 1
        : b.lastMessage.sentAt < a.lastMessage.sentAt
          ? -1
          : 0,
    );

    if (typeof filterFn === "function") {
      preparedChatRooms = filterFn(preparedChatRooms);
    }

    if (!isUnmounted.current) {
      // Safety net for the same race as the regular chat list's fetch: a
      // refetch landing between "open chat" and the `readGlobalMessages`
      // round-trip completing would otherwise show the server's
      // not-yet-updated unread count for the chat the user has open.
      setGlobalChatList(
        preparedChatRooms.map((room) =>
          room.id === openedChatRoomIdRef.current
            ? { ...room, unreadMessageCount: 0 }
            : room,
        ),
      );
      setIsLoading(false);
    }
  }, [
    enabled,
    profile,
    isPopulateLastMessage,
    isPopulateChatParticipants,
    isPopulateChatParticipantProfiles,
    filterFn,
    chatListRefresh, // if you expect refresh to re-fetch
  ]);

  // initial + refresh fetch
  useEffect(() => {
    fetchGlobalChatList();
  }, [fetchGlobalChatList]);

  // emit read event
  useEffect(() => {
    if (!enabled) return;
    if (!allowReadMessageEventEmitting) return;
    if (
      !socketForGlobalMessage?.current ||
      socketForGlobalMessage.current.disconnected
    )
      return;
    if (!openedChatRoomId) return;
    if (!chatId) return;

    socketForGlobalMessage.current.emit("readGlobalMessages", openedChatRoomId);
  }, [
    enabled,
    allowReadMessageEventEmitting,
    socketForGlobalMessage,
    openedChatRoomId,
    chatId,
  ]);

  // socket subscriptions (guarded)
  useEffect(() => {
    if (!enabled) return;
    if (
      !socketForGlobalMessage?.current ||
      socketForGlobalMessage.current.disconnected
    )
      return;

    const newChatRoomHandler = async (chatRoom) => {
      // avoid duplicates
      if (!Array.isArray(globalChatList)) return;
      if (globalChatList.find((chat) => chat.id === chatRoom.id)) return;

      if (
        !Array.isArray(chatRoom.participants) ||
        chatRoom.participants.length < 2
      ) {
        pendingNewChats.current = [...pendingNewChats.current, chatRoom];
        return;
      }

      const preparedChatRoom = await prepareGlobalChatRoom(
        chatRoom,
        profile,
        isPopulateChatParticipantProfiles,
        isAllAccountsLogin,
      );
      if (isUnmounted.current) return;

      setGlobalChatList((prev = []) => [preparedChatRoom, ...prev]);
    };

    const newChatParticipantHandler = async (chatParticipant) => {
      const pendingChat = pendingNewChats.current.find(
        (v) => v.id === chatParticipant.globalChatRoomId,
      );
      if (!pendingChat) return;

      if (
        Array.isArray(globalChatList) &&
        globalChatList.find((chat) => chat.id === pendingChat.id)
      ) {
        pendingNewChats.current = pendingNewChats.current.filter(
          (v) => v.id !== pendingChat.id,
        );
        return;
      }

      // rebuild full list to ensure consistency
      await fetchGlobalChatList();
      pendingNewChats.current = pendingNewChats.current.filter(
        (v) => v.id !== pendingChat.id,
      );
    };

    const newMessageHandler = async (chatMessage) => {
      setGlobalChatList((prev = []) => {
        const match = prev.find((c) => c.id === chatMessage.globalChatRoomId);
        if (!match) return prev;

        const params = new URLSearchParams(window.location.search);
        const chatIdParams = params.get("chatId");

        const isOpenChat =
          chatMessage.globalChatRoomId === openedChatRoomId &&
          chatIdParams &&
          allowReadMessageEventEmitting;

        let unreadInc = 0;
        if (chatMessage.senderId !== profile.userId) {
          if (isOpenChat) {
            socketForGlobalMessage.current.emit(
              "readGlobalMessages",
              chatMessage.globalChatRoomId,
            );
          } else {
            unreadInc = 1;
          }
        }

        const updated = {
          ...match,
          lastMessage: { ...chatMessage, sentAt: Number(chatMessage.sentAt) },
          unreadMessageCount: isOpenChat
            ? 0
            : (match.unreadMessageCount || 0) + unreadInc,
        };

        return [
          updated,
          ...prev.filter((c) => c.id !== chatMessage.globalChatRoomId),
        ];
      });
    };

    const messagesReadHandler = (readEvent) => {
      setGlobalChatList((prev = []) => {
        const idx = prev.findIndex((c) => c.id === readEvent.globalChatRoomId);
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], unreadMessageCount: 0 };
        return copy;
      });
    };

    const chatRoomDeletedHandler = (globalChatRoomId) => {
      setGlobalChatList((prev = []) =>
        prev.filter((c) => c.id !== globalChatRoomId),
      );
    };

    socketForGlobalMessage.current.on("newGlobalChatRoom", newChatRoomHandler);
    socketForGlobalMessage.current.on(
      "newGlobalChatParticipant",
      newChatParticipantHandler,
    );
    socketForGlobalMessage.current.on(
      "newGlobalChatMessage",
      newMessageHandler,
    );
    socketForGlobalMessage.current.on(
      "globalMessagesRead",
      messagesReadHandler,
    );
    socketForGlobalMessage.current.on(
      "globalChatRoomDeleted",
      chatRoomDeletedHandler,
    );
    socketForGlobalMessage.current.on(
      "globalParticipantChatListUpdate",
      fetchGlobalChatList,
    );

    return () => {
      socketForGlobalMessage.current.off(
        "newGlobalChatRoom",
        newChatRoomHandler,
      );
      socketForGlobalMessage.current.off(
        "newGlobalChatParticipant",
        newChatParticipantHandler,
      );
      socketForGlobalMessage.current.off(
        "newGlobalChatMessage",
        newMessageHandler,
      );
      socketForGlobalMessage.current.off(
        "globalMessagesRead",
        messagesReadHandler,
      );
      socketForGlobalMessage.current.off(
        "globalChatRoomDeleted",
        chatRoomDeletedHandler,
      );
      socketForGlobalMessage.current.off(
        "globalParticipantChatListUpdate",
        fetchGlobalChatList,
      );
    };
  }, [
    enabled,
    socketForGlobalMessage,
    profile,
    openedChatRoomId,
    allowReadMessageEventEmitting,
    isPopulateChatParticipantProfiles,
    fetchGlobalChatList,
    globalChatList, // OK if you want resubscribe on list changes; otherwise use refs
  ]);

  // updater
  const updateGlobalChatRoom = useCallback(async (accountId, chatRoom, cb) => {
    setGlobalChatList(async (prevList) => {
      const list = [...(prevList || [])];
      const idx = list.findIndex((c) => c.id === chatRoom.id);
      if (idx === -1) return list;

      const updateResponse = await chatGraphqlInstance.updateChatChatRoomGlobal(
        {
          id: chatRoom.id,
          isArchived: chatRoom.isArchived,
        },
      );

      list[idx] = {
        ...list[idx],
        isArchived: updateResponse.isArchived,
        organizerName: updateResponse.organizerName,
      };

      if (typeof cb === "function") cb();
      return list;
    });
  }, []);

  // Final derived return (don’t early-return above)
  return useMemo(() => {
    const chats = (globalChatList || []).filter(
      (chat, index, arr) => arr.findIndex((c) => c.id === chat.id) === index,
    );

    // If not enabled, surface an empty list + not loading,
    // but the hook still ran consistently.
    if (!enabled) {
      return [[], { isLoading: false, updateGlobalChatRoom }];
    }
    return [chats, { isLoading, updateGlobalChatRoom }];
  }, [enabled, globalChatList, isLoading, updateGlobalChatRoom]);
};
